import {
  STAFF_CATALOG_VERIFICATION_STALE_DAYS,
  type StaffCatalogItemStatus,
  type StaffCatalogItemType,
  type StaffCatalogPriceClass,
  staffCatalogDressCodes,
  staffCatalogEventStatuses,
  staffCatalogExperiencePrivacies,
  staffCatalogFulfilmentModes,
  staffCatalogItemStatuses,
  staffCatalogItemTypes,
  staffCatalogNoteKinds,
  staffCatalogPersonRoles,
  staffCatalogPriceSources,
  staffCatalogPriceUnits,
  staffCatalogPricingModels,
  type staffCatalogPublishabilityIssueCodes,
  staffCatalogReservationPolicies,
  staffCatalogSchedulePrecisions,
  staffCatalogSorts,
  staffCatalogSourceKinds,
  staffCatalogTagNamespaces,
  staffCatalogVerificationFilters,
} from "@lafayette/schemas";

// Plain-language labels for every code the contract sends. Each table is a `Record` over the contract's
// tuple, so a new code in the contract fails the type check here until it has a label.

export type Option<T extends string = string> = Readonly<{ value: T; label: string }>;

export function optionsOf<T extends string>(
  values: readonly T[],
  labels: Readonly<Record<T, string>>,
): readonly Option<T>[] {
  return values.map((value) => ({ value, label: labels[value] }));
}

export const itemTypeLabels: Readonly<Record<(typeof staffCatalogItemTypes)[number], string>> = {
  DINING: "Dining",
  VILLA: "Villa",
  EVENT: "Event",
  EXPERIENCE: "Experience",
  SERVICE: "Service",
};

export const itemTypeDescriptions: Readonly<Record<StaffCatalogItemType, string>> = {
  DINING: "A restaurant, bar or cafe.",
  VILLA: "A villa or a stay with a fixed capacity.",
  EVENT: "Something with a date and a venue.",
  EXPERIENCE: "A guided or timed activity.",
  SERVICE: "A service Lafayette arranges in an area.",
};

export const itemStatusLabels: Readonly<Record<StaffCatalogItemStatus, string>> = {
  DRAFT: "Draft",
  REVIEW: "In review",
  PUBLISHED: "Published",
  UNPUBLISHED: "Unpublished",
  ARCHIVED: "Archived",
};

/** A glyph beside the status word, so the status never depends on colour alone. */
export const statusGlyphs: Readonly<Record<StaffCatalogItemStatus, string>> = {
  DRAFT: "○",
  REVIEW: "◐",
  PUBLISHED: "●",
  UNPUBLISHED: "◌",
  ARCHIVED: "▪",
};

export const pricingModelLabels: Readonly<
  Record<(typeof staffCatalogPricingModels)[number], string>
> = {
  UNKNOWN: "Unknown",
  INFORMATION_ONLY: "Information only",
  FIXED: "Fixed price",
  FROM: "From price",
  RANGE: "Price range",
  QUOTE_REQUIRED: "Quote required",
};

export const priceClassLabels: Readonly<Record<StaffCatalogPriceClass, string>> = {
  PAYMENT_GRADE: "Payment-grade",
  FIXED_NOT_PAYABLE: "Fixed, not payable",
  INDICATIVE: "Indicative",
  QUOTE: "Quote",
  UNKNOWN: "Unknown",
};

export const priceClassDescriptions: Readonly<Record<StaffCatalogPriceClass, string>> = {
  PAYMENT_GRADE:
    "A customer can pay this price. A price change removes this mark until the price is authorized again.",
  FIXED_NOT_PAYABLE:
    "The price is fixed, but a customer cannot pay it yet. The reasons are listed below when they are known.",
  INDICATIVE: "Customers see this price as a guide only.",
  QUOTE: "A concierge gives a quote for this item.",
  UNKNOWN: "No price is set.",
};

const fulfilmentLabels: Readonly<Record<(typeof staffCatalogFulfilmentModes)[number], string>> = {
  INFORMATION_ONLY: "Information only",
  HUMAN_CONCIERGE: "Human concierge",
  EXTERNAL_LINK: "External link",
  CUSTOMER_SELF_SERVICE: "Customer self-service",
  DIRECT_API: "Direct API",
};

export const sourceKindLabels: Readonly<Record<(typeof staffCatalogSourceKinds)[number], string>> =
  {
    EDITORIAL: "Editorial",
    IMPORT: "Import",
    PROVIDER_ENRICHMENT: "Provider enrichment",
    LEGACY_MIGRATION: "Legacy migration",
  };

export const priceUnitLabels: Readonly<Record<(typeof staffCatalogPriceUnits)[number], string>> = {
  PER_PERSON: "Per person",
  PER_GROUP: "Per group",
  PER_BOOKING: "Per booking",
  PER_NIGHT: "Per night",
  PER_DAY: "Per day",
  PER_HOUR: "Per hour",
  PER_ITEM: "Per item",
};

const priceSourceLabels: Readonly<Record<(typeof staffCatalogPriceSources)[number], string>> = {
  SUPPLIER_CONTRACT: "Supplier contract",
  SUPPLIER_PUBLISHED: "Supplier published price",
  EDITORIAL_ESTIMATE: "Editorial estimate",
  IMPORTED: "Imported",
};

export const tagNamespaceLabels: Readonly<
  Record<(typeof staffCatalogTagNamespaces)[number], string>
> = {
  CUISINE: "Cuisine",
  DIETARY: "Dietary",
  AMBIENCE: "Ambience",
  MEAL_PERIOD: "Meal period",
  AMENITY: "Amenity",
  FEATURE: "Feature",
  RECOMMENDED_FOR: "Recommended for",
  GENERAL: "General",
};

export const personRoleLabels: Readonly<Record<(typeof staffCatalogPersonRoles)[number], string>> =
  {
    PERFORMER: "Performer",
    HOST: "Host",
    CHEF: "Chef",
    GUIDE: "Guide",
    ARTIST: "Artist",
    OTHER: "Other",
  };

export const noteKindLabels: Readonly<Record<(typeof staffCatalogNoteKinds)[number], string>> = {
  EDITORIAL: "Editorial",
  SUPPLIER_CONTACT: "Supplier contact",
  PRICING_CONTEXT: "Pricing context",
  FULFILMENT: "Fulfilment",
  VERIFICATION: "Verification",
};

const dressCodeLabels: Readonly<Record<(typeof staffCatalogDressCodes)[number], string>> = {
  CASUAL: "Casual",
  SMART_CASUAL: "Smart casual",
  SMART: "Smart",
  FORMAL: "Formal",
  BLACK_TIE: "Black tie",
};

const reservationPolicyLabels: Readonly<
  Record<(typeof staffCatalogReservationPolicies)[number], string>
> = {
  REQUIRED: "Reservation required",
  RECOMMENDED: "Reservation recommended",
  NOT_REQUIRED: "Reservation not required",
  WALK_IN_ONLY: "Walk-in only",
};

const eventStatusLabels: Readonly<Record<(typeof staffCatalogEventStatuses)[number], string>> = {
  SCHEDULED: "Scheduled",
  POSTPONED: "Postponed",
  CANCELLED: "Cancelled",
  SOLD_OUT: "Sold out",
};

export const schedulePrecisionLabels: Readonly<
  Record<(typeof staffCatalogSchedulePrecisions)[number], string>
> = {
  EXACT: "Exact time",
  DATE_ONLY: "Date only",
};

const privacyLabels: Readonly<Record<(typeof staffCatalogExperiencePrivacies)[number], string>> = {
  PRIVATE: "Private",
  SHARED: "Shared",
  EITHER: "Private or shared",
};

export const verificationFilterLabels: Readonly<
  Record<(typeof staffCatalogVerificationFilters)[number], string>
> = {
  never: "Never verified",
  stale: `Stale (over ${STAFF_CATALOG_VERIFICATION_STALE_DAYS} days)`,
  fresh: "Fresh",
};

const sortLabels: Readonly<Record<(typeof staffCatalogSorts)[number], string>> = {
  updated: "Recently updated",
  name: "Name (A to Z)",
};

export const statusOptions = optionsOf(staffCatalogItemStatuses, itemStatusLabels);
export const itemTypeOptions = optionsOf(staffCatalogItemTypes, itemTypeLabels);
export const pricingModelOptions = optionsOf(staffCatalogPricingModels, pricingModelLabels);
export const fulfilmentOptions = optionsOf(staffCatalogFulfilmentModes, fulfilmentLabels);
export const sourceKindOptions = optionsOf(staffCatalogSourceKinds, sourceKindLabels);
export const priceUnitOptions = optionsOf(staffCatalogPriceUnits, priceUnitLabels);
export const priceSourceOptions = optionsOf(staffCatalogPriceSources, priceSourceLabels);
export const tagNamespaceOptions = optionsOf(staffCatalogTagNamespaces, tagNamespaceLabels);
export const personRoleOptions = optionsOf(staffCatalogPersonRoles, personRoleLabels);
export const noteKindOptions = optionsOf(staffCatalogNoteKinds, noteKindLabels);
export const dressCodeOptions = optionsOf(staffCatalogDressCodes, dressCodeLabels);
export const reservationPolicyOptions = optionsOf(
  staffCatalogReservationPolicies,
  reservationPolicyLabels,
);
export const eventStatusOptions = optionsOf(staffCatalogEventStatuses, eventStatusLabels);
export const schedulePrecisionOptions = optionsOf(
  staffCatalogSchedulePrecisions,
  schedulePrecisionLabels,
);
export const privacyOptions = optionsOf(staffCatalogExperiencePrivacies, privacyLabels);
export const verificationOptions = optionsOf(
  staffCatalogVerificationFilters,
  verificationFilterLabels,
);
export const sortOptions = optionsOf(staffCatalogSorts, sortLabels);

// --- publishability --------------------------------------------------------------------------------

/** The part of the edit page that fixes an issue. The ids are the `id` of each section. */
export type SectionId =
  | "details"
  | "type-details"
  | "pricing"
  | "taxonomy"
  | "media"
  | "verification";

export type IssueInfo = Readonly<{ message: string; section: SectionId }>;

export const publishabilityIssueInfo: Readonly<
  Record<(typeof staffCatalogPublishabilityIssueCodes)[number], IssueInfo>
> = {
  MISSING_SUBTYPE: { message: "The type details are not saved yet.", section: "type-details" },
  MISSING_SHORT_DESCRIPTION: { message: "Add a short description.", section: "details" },
  MISSING_DESCRIPTION: { message: "Add a description.", section: "details" },
  MISSING_LOCATION: {
    message: "Add a location: a locality and a country, or an address.",
    section: "details",
  },
  MISSING_COVER_IMAGE: { message: "Add a cover image.", section: "media" },
  MEDIA_ALT_TEXT_MISSING: { message: "Add alt text to every image.", section: "media" },
  MISSING_PRIMARY_CATEGORY: { message: "Choose a primary category.", section: "taxonomy" },
  DINING_MISSING_CUISINE: { message: "Add at least one cuisine tag.", section: "taxonomy" },
  VILLA_MISSING_CAPACITY: { message: "Set the maximum number of guests.", section: "type-details" },
  VILLA_TIMES_WITHOUT_ZONE: {
    message: "Check-in and check-out times need a time zone for the item.",
    section: "details",
  },
  EVENT_MISSING_VENUE: { message: "Add the venue name.", section: "type-details" },
  EVENT_ENDED: { message: "The event has already ended.", section: "type-details" },
  EXPERIENCE_MISSING_DURATION: { message: "Set the duration.", section: "type-details" },
  SERVICE_MISSING_AREA: { message: "Describe the service area.", section: "type-details" },
  FULFILMENT_MODE_NOT_ENABLED: {
    message: "This fulfilment mode cannot be used yet. Choose another one.",
    section: "details",
  },
  EXTERNAL_LINK_MISSING: {
    message: "An external-link item needs an external URL.",
    section: "details",
  },
  PRICING_INCOMPLETE: {
    message: "The price is incomplete for its pricing model.",
    section: "pricing",
  },
  CUSTOMER_TEXT_CONTAINS_INTERNAL_NOTE: {
    message: "Customer text repeats an internal note. Remove the repeated text.",
    section: "details",
  },
  EXTERNAL_URL_NOT_ALLOWED: {
    message: "The external URL is not allowed. Use an https address.",
    section: "details",
  },
  PRICE_AUTHORITY_EXPIRED: {
    message: "The price authorization has expired. Authorize the price again.",
    section: "pricing",
  },
};

// --- history ---------------------------------------------------------------------------------------

const actionLabels: Readonly<Record<string, string>> = {
  "catalog_item.created": "Item created",
  "catalog_item.details_updated": "Details updated",
  "catalog_item.subtype_updated": "Type details updated",
  "catalog_item.pricing_updated": "Price updated",
  "catalog_item.price_authorized": "Price authorized for payment",
  "catalog_item.categories_replaced": "Categories updated",
  "catalog_item.tags_replaced": "Tags updated",
  "catalog_item.people_replaced": "People updated",
  "catalog_item.media_replaced": "Media updated",
  "catalog_item.external_ref_added": "External reference added",
  "catalog_item.verified": "Marked as verified",
  "catalog_item.internal_note_added": "Internal note added",
  "catalog_item.submitted_for_review": "Submitted for review",
  "catalog_item.returned_to_draft": "Returned to draft",
  "catalog_item.published": "Published",
  "catalog_item.unpublished": "Unpublished",
  "catalog_item.archived": "Archived",
};

/** A readable name for an audit action. An action this table does not know is shown as its code. */
export function historyActionLabel(action: string): string {
  return actionLabels[action] ?? action;
}

// --- verification freshness --------------------------------------------------------------------------

export type VerificationFreshness = "never" | "stale" | "fresh";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The same rule as the API's `verification` filter: older than 180 days is stale. */
export function verificationFreshness(
  lastVerifiedAt: Date | null,
  now: Date,
): VerificationFreshness {
  if (lastVerifiedAt === null) return "never";
  const ageMs = now.getTime() - lastVerifiedAt.getTime();
  return ageMs > STAFF_CATALOG_VERIFICATION_STALE_DAYS * DAY_MS ? "stale" : "fresh";
}

// --- dates -------------------------------------------------------------------------------------------

const dateTimeFormat = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "UTC",
});
const dateFormat = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeZone: "UTC" });

/** Dates are shown in UTC and say so: staff in different zones read the same time. */
export function formatDateTime(value: Date): string {
  return `${dateTimeFormat.format(value)} UTC`;
}

export function formatDate(value: Date): string {
  return dateFormat.format(value);
}

/** `snake_case` or `camelCase` to words, for a metadata key the label tables do not know. */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.length === 0 ? key : words.charAt(0).toUpperCase() + words.slice(1);
}
